import { FC, useMemo, useState } from "react";

import { Card, HStack, Pressable, Text, VStack } from "@/components/ui";

import { formatMargin, formatPercent } from "./colors";
import InfoTip from "./InfoTip";
import { byMisses, CountSwatch, Swatch, Tag } from "./RaceList";
import {
  districtMatchup,
  districtSides,
  districtVerdict,
  type DistrictRow,
  type Verdict,
} from "./view";

interface DistrictListProps {
  /** Opens the district's state (national list); null inside a state. */
  onSelect: ((state: string) => void) | null;
  /** The compared source is the count. */
  results: boolean;
  rows: DistrictRow[];
  sourceLabel: string | null;
  title: string;
}

/** Closest seats shown before "Show all" in the national list. */
const COLLAPSED = 25;

const GAP_HINT =
  "Gap: Foresight's margin minus the source's, in points (D+3.0: Foresight is 3 points more Democratic).";

const HINT =
  "Every district as drawn for 2026. Foresight moves each district's 2024 presidential result by its state's swing, plus the district's own deviation once its counties have enough respondents. Seats whose finalists share a party, or with a single candidate, are decided. A striped swatch and the FLIP tag mark seats changing party.";

const RESULTS_HINT =
  "Every district as drawn for 2026, Foresight's margin and call next to the count's. Gap: Foresight's margin minus the count's, in points of the two-party vote (R+3.0: Foresight was 3 points more Republican). A striped swatch and the MISS tag mark seats Foresight called wrong.";

/** Closest contested seats first, decided seats last. */
const byCloseness = (rows: DistrictRow[]) =>
  [...rows].sort((a, b) => {
    const pa = a.decided ? null : a.call.leftProbability;
    const pb = b.decided ? null : b.call.leftProbability;
    if (pa === null && pb === null)
      return a.district.id.localeCompare(b.district.id);
    if (pa === null) return 1;
    if (pb === null) return -1;
    return Math.abs(pa - 0.5) - Math.abs(pb - 0.5);
  });

const DistrictLine: FC<{
  row: DistrictRow;
  sourceLabel: string | null;
  verdict: Verdict | null;
}> = ({ row, sourceLabel, verdict }) => {
  const { decided, district, projection } = row;
  const [left, right] = districtSides(district);
  const sourceMargin = row.external?.margin ?? null;
  const gap =
    verdict?.gap ??
    (projection && sourceMargin !== null
      ? projection.margin - sourceMargin
      : null);
  return (
    <>
      {verdict ? (
        <CountSwatch verdict={verdict} />
      ) : (
        <Swatch call={row.call} flip={row.flip} />
      )}
      <VStack className="flex-1 min-w-0">
        <HStack className="items-center gap-1.5">
          <Text className="text-sm font-medium text-typography-900">
            {district.id}
          </Text>
          {verdict
            ? verdict.miss && <Tag label="MISS" />
            : row.flip && <Tag label="FLIP" />}
        </HStack>
        <Text className="text-xs text-typography-500" numberOfLines={1}>
          {districtMatchup(district)} · held by {district.holder} ·{" "}
          {formatMargin((2 * district.harris2024 - 1) * 100)} in 2024
        </Text>
      </VStack>
      <VStack className="w-20 items-end">
        <Text className="text-sm font-semibold text-typography-900">
          {decided
            ? `Safe ${decided}`
            : projection
              ? formatMargin(projection.margin, left, right)
              : "—"}
        </Text>
        {projection && (
          <Text className="text-xs text-typography-500">
            {verdict
              ? `call ${verdict.ours ?? "even"}`
              : `${formatPercent(projection.leftProbability)} ${left}`}
          </Text>
        )}
      </VStack>
      {sourceLabel && (
        <>
          {verdict ? (
            <VStack className="w-20 items-end">
              <Text className="text-sm text-typography-700">
                {formatMargin(sourceMargin, left, right)}
              </Text>
              {row.external?.result && (
                <Text className="text-xs text-typography-500">
                  {verdict.actual
                    ? `called ${verdict.actual}`
                    : `${row.external.result.reporting}% in`}
                </Text>
              )}
            </VStack>
          ) : (
            <Text className="w-20 text-right text-sm text-typography-700">
              {decided
                ? `Safe ${decided}`
                : row.externalProbability === null
                  ? "—"
                  : `${formatPercent(row.externalProbability)} ${left}`}
            </Text>
          )}
          <Text className="w-12 text-right text-xs text-typography-500">
            {gap === null ? "—" : formatMargin(gap, left, right)}
          </Text>
        </>
      )}
    </>
  );
};

/** House districts: closest first nationally (misses first against the
    count), by number within a state. */
const DistrictList: FC<DistrictListProps> = ({
  onSelect,
  results,
  rows,
  sourceLabel,
  title,
}) => {
  const [expanded, setExpanded] = useState(false);
  const sorted = useMemo<{ item: DistrictRow; verdict: Verdict | null }[]>(
    () =>
      results && onSelect
        ? byMisses(rows, districtVerdict)
        : (onSelect ? byCloseness(rows) : rows).map((item) => ({
            item,
            verdict: results ? districtVerdict(item) : null,
          })),
    [onSelect, results, rows]
  );
  const shown = onSelect && !expanded ? sorted.slice(0, COLLAPSED) : sorted;
  const flips = rows.filter((row) => row.flip).length;
  const judged = sorted.filter(
    ({ verdict }) => verdict?.actual && verdict.ours
  ).length;
  const misses = sorted.filter(({ verdict }) => verdict?.miss).length;

  return (
    <Card className="p-5 rounded-2xl border border-outline-200">
      <VStack className="gap-3">
        <HStack className="items-baseline justify-between gap-2">
          <HStack className="flex-1 items-center gap-1">
            <Text className="text-sm font-semibold text-typography-900">
              {title}
            </Text>
            <InfoTip
              text={[
                results ? RESULTS_HINT : HINT,
                ...(sourceLabel && !results ? [GAP_HINT] : []),
                ...(onSelect ? ["Click a row to open the state."] : []),
              ].join(" ")}
            />
          </HStack>
          <Text className="text-xs text-typography-500">
            {results
              ? `${misses} missed of ${judged} called`
              : `${flips} projected flip${flips === 1 ? "" : "s"}`}
          </Text>
        </HStack>

        <HStack className="px-1 gap-2">
          <Text className="flex-1 text-xs text-typography-400">District</Text>
          <Text className="w-20 text-right text-xs text-typography-400">
            Foresight
          </Text>
          {sourceLabel && (
            <>
              <Text
                className="w-20 text-right text-xs text-typography-400"
                numberOfLines={1}
              >
                {results ? "Count" : sourceLabel}
              </Text>
              <Text className="w-12 text-right text-xs text-typography-400">
                Gap
              </Text>
            </>
          )}
        </HStack>

        <VStack className="gap-0.5">
          {shown.map(({ item: row, verdict }) =>
            onSelect ? (
              <Pressable
                className="flex-row items-center gap-2 px-1 py-1.5 rounded-md hover:bg-background-50"
                key={row.district.id}
                onPress={() => onSelect(row.district.state)}
              >
                <DistrictLine
                  row={row}
                  sourceLabel={sourceLabel}
                  verdict={verdict}
                />
              </Pressable>
            ) : (
              <HStack
                className="items-center gap-2 px-1 py-1.5"
                key={row.district.id}
              >
                <DistrictLine
                  row={row}
                  sourceLabel={sourceLabel}
                  verdict={verdict}
                />
              </HStack>
            )
          )}
        </VStack>

        {onSelect && sorted.length > COLLAPSED && (
          <Pressable
            className="self-start px-1"
            onPress={() => setExpanded((open) => !open)}
          >
            <Text className="text-xs font-medium text-primary-600">
              {expanded
                ? results
                  ? "Show the first only"
                  : "Show the closest only"
                : `Show all ${sorted.length} districts`}
            </Text>
          </Pressable>
        )}
      </VStack>
    </Card>
  );
};

export default DistrictList;
